import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getStoredLanguage } from '../utils/language';
import { isStandaloneMode, markInstalledApp } from '../utils/pwa';

const INSTALL_DISMISSED_KEY = 'iadvocate-install-dismissed-this-session';

const AppEntry = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(INSTALL_DISMISSED_KEY) === '1';
    } catch (error) {
      return false;
    }
  });

  const nextPath = useMemo(() => (getStoredLanguage() ? '/login' : '/language'), []);
  const shouldNudgeInstall = Boolean(installPrompt) && !isStandaloneMode() && !installDismissed;

  useEffect(() => {
    if (isStandaloneMode()) {
      markInstalledApp();
      navigate(nextPath, { replace: true });
    }

    const handleBeforeInstallPrompt = (event) => {
      if (window.matchMedia?.('(max-width: 760px)').matches) {
        event.preventDefault();
        setInstallPrompt(event);
      }
    };

    const handleInstalled = () => {
      markInstalledApp();
      setInstallPrompt(null);
      navigate(nextPath, { replace: true });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, [navigate, nextPath]);

  useEffect(() => {
    if (!isStandaloneMode() && !shouldNudgeInstall) {
      navigate(nextPath, { replace: true });
    }
  }, [navigate, nextPath, shouldNudgeInstall]);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice.catch(() => null);
      setInstallPrompt(null);
      return;
    }
  };

  const dismissInstallPrompt = () => {
    try {
      window.sessionStorage.setItem(INSTALL_DISMISSED_KEY, '1');
    } catch (error) {
      // Ignore storage issues and keep this a soft prompt.
    }
    setInstallPrompt(null);
    setInstallDismissed(true);
  };

  return (
    <div className="auth-screen">
      {shouldNudgeInstall ? (
        <div className="app-modal app-modal--install">
          <button type="button" className="app-modal__scrim" aria-label={t('installContinueBrowser')} onClick={dismissInstallPrompt} />
          <div className="app-modal__surface app-modal__surface--install">
            <p className="eyebrow">{t('installAppEyebrow')}</p>
            <h2>{t('installPromptReadyTitle')}</h2>
            <p>{t('installPromptReadyBody')}</p>
            <div className="button-row top-space">
              <button type="button" className="button" onClick={handleInstall}>
                {t('installPromptButton')}
              </button>
              <button type="button" className="button button--secondary" onClick={dismissInstallPrompt}>
                {t('installContinueBrowser')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="auth-layout auth-layout--entry">
        <section className="auth-hero auth-hero--dark auth-hero--entry">
          <div className="auth-hero__body auth-hero__body--entry">
            <img
              className="auth-hero__logo auth-hero__logo--entry"
              src="https://upload.wikimedia.org/wikipedia/commons/e/e6/Emblem_of_the_Supreme_Court_of_India.svg"
              alt="Supreme Court of India emblem"
            />
            <div className="auth-hero__copy">
              <p className="brand-mark brand-mark--light">{t('appName')}</p>
            </div>
          </div>
        </section>
        <div className="auth-card auth-card--entry">
          <p className="eyebrow">{t('installAppEyebrow')}</p>
          <h2>{t('installAppTitle')}</h2>
          <p className="helper-text">{t('installAppSubtitle')}</p>
        </div>
      </div>
    </div>
  );
};

export default AppEntry;
